import { useEffect, useMemo, useState } from 'react';
import { getRolesAndPermissionsColumns } from './columns';
import { CommonDatagrid } from '..';
import { useDispatch, useSelector } from 'react-redux';
import { IRoleContent, IRootState } from '../../../types/store';
import { CreateEditRoleDialog } from '../../Dialog/CreateEditRoleDialog';
import { ViewRoleDialog } from '../../Dialog/ViewRoleDialog';
import { RefreshButton } from '../../Buttons/RefreshButton';
import { RolesAndPermissionsService } from '../../../api/services/rolesAndPermissionsService';
import {
  deleteRole,
  setRolesAndPermissions,
} from '../../../store/Reducers/RolesAndPermissionsSlice';
import { Box, Button } from '@mui/material';
import { PermissionsService } from '../../../api/services/permissionsService';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { setPermissions } from '../../../store/Reducers/PermissionsSlice';
import { showAlertDialog } from '../../../store/Reducers/AlertDialogSlice';

export const RolesAndPermissionsDatagrid = () => {
  const [selectedRole, setSelectedRole] = useState<IRoleContent | null>(null);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  const [isOpenViewDialog, setIsOpenViewDialog] = useState(false);

  const { content } = useSelector((state: IRootState) => state.rolesAndPermissions);
  const dispatch = useDispatch();

  const onClickEdit = (role: IRoleContent) => {
    setSelectedRole(role);
    setIsOpenEditDialog(true);
  };

  const onClickView = (role: IRoleContent) => {
    setSelectedRole(role);
    setIsOpenViewDialog(true);
  };

  const onClickDelete = (role: IRoleContent) => {
    dispatch(
      showAlertDialog({
        title: 'Delete Role?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        onConfirm: () => handleDelete(role.id),
      }),
    );
  };

  const handleDelete = async (id: number) => {
    try {
      await RolesAndPermissionsService.deleteRole(id);
      dispatch(deleteRole(id));
      dispatch(showNotification({ message: 'Successfully deleted role.' }));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to delete role. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const rolesAndPermissionsColumns = useMemo(() => {
    return getRolesAndPermissionsColumns(onClickEdit, onClickView, onClickDelete);
  }, []);

  const reloadData = async () => {
    try {
      const res = await RolesAndPermissionsService.getAllRoles();
      dispatch(setRolesAndPermissions(res));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve latest list of Roles. ${e?.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const reloadPermissions = async () => {
    try {
      const permissions = await PermissionsService.getAllPermissions();
      dispatch(setPermissions(permissions));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve latest list of Permissions. ${e?.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    reloadPermissions();
    reloadData();
  }, []);

  return (
    <>
      <CommonDatagrid
        header={<p>Roles</p>}
        data={content}
        columns={rolesAndPermissionsColumns}
        rowId={'id'}
        searchbarPlaceholder={'Search role by name'}
        searchKey={'name'}
        headerActionButtons={
          <Box display="flex" gap={2}>
            <RefreshButton onClick={reloadData} />
            <Button
              variant="contained"
              sx={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
              onClick={() => {
                setSelectedRole(null);
                setIsOpenEditDialog(true);
              }}
            >
              Add New Role
            </Button>
          </Box>
        }
      />
      <CreateEditRoleDialog
        selectedRole={selectedRole}
        isOpenDialog={isOpenEditDialog}
        setIsOpenDialog={(open) => {
          setIsOpenEditDialog(open);
          if (!open) setSelectedRole(null);
        }}
      />
      <ViewRoleDialog
        role={selectedRole}
        isOpen={isOpenViewDialog}
        onClose={() => {
          setIsOpenViewDialog(false);
          setSelectedRole(null);
        }}
      />
    </>
  );
};
